import { MOBS, MOB_AFFIXES, type MobAttack, type MobDef } from '../content/mobs';
import { PETS, petLevelMul } from '../content/pets';
import { WEAPONS } from '../content/items';
import { SKILLS } from '../content/skills';
import { BASE_STATS, BOSS_RANK_XP, applyModifiers, type Modifier } from '../content/stats';
import type { Tag } from '../content/tags';
import type { Action, ProjectileAction, AreaAction, StatusApply } from '../content/actions';
import { HEROES } from '../content/heroes';
import { wrapAngle, dist } from '../math';
import type { World } from './world';
import { TPS } from './world';
import { REVENANT_ITEM_CHANCE, type AnyUnit, type EntityId, type GhostSnapshot, type MobEnt, type PlayerEnt } from './types';
import { runAction, fireHooks } from './actions';
import { applyStatus, hasStatus, isStunned, speedMul, stacksOf } from './status';
import { dealDamage, heal } from './combat';
import { addXp, addRankXp, itemFromSave, playerPower, scaledSkill } from './player';
import { dropLoot, spawnPickup } from './loot';
import { addCorruption, onBossKilledRealm } from './realm';

export interface SpawnMobOpts {
  elite?: boolean;
  affixes?: string[];
  team?: number;
  owner?: EntityId;
  until?: number;
  hpScale?: number;
  dmgScale?: number;
  kind?: MobEnt['kind'] | 'summon';
  name?: string;
  level?: number;
}

const revenantDefs = new Map<string, MobDef>();

function mobStats(def: MobDef, affixes: string[], elite: boolean, hpScale: number) {
  const mods: Modifier[] = [];
  for (const a of affixes) mods.push(...(MOB_AFFIXES[a]?.mods ?? []));
  // bosses are tuned directly in their definitions; the elite flag only marks loot/visuals
  if (elite && !def.boss) mods.push({ stat: 'maxHp', more: 2.2 }, { stat: 'damage', more: 1.3 });
  const base = { ...BASE_STATS, maxHp: def.hp * hpScale, moveSpeed: def.speed, hpRegen: 0, critChance: 0, pickupRadius: 0 };
  return applyModifiers(base, mods);
}

export function spawnMob(world: World, defId: string, x: number, y: number, o: SpawnMobOpts = {}): MobEnt {
  const def = defOf(defId);
  const id = world.allocId();
  const elite = !!(o.elite || def.elite);
  const affixes = o.affixes ?? [];
  const stats = mobStats(def, affixes, elite, o.hpScale ?? 1);
  const kind: MobEnt['kind'] = o.kind ?? (def.boss ? 'boss' : 'mob');
  const pos = world.map.nearestWalkable(x, y, def.r, 8);
  const m: MobEnt = {
    id,
    kind,
    x: pos.x,
    y: pos.y,
    r: def.r * (elite && !def.boss ? 1.25 : 1),
    team: o.team ?? 0,
    facing: world.rng.angle(),
    hp: stats.maxHp,
    maxHp: stats.maxHp,
    stats,
    statuses: [],
    shield: 0,
    dead: false,
    vx: 0,
    vy: 0,
    knockX: 0,
    knockY: 0,
    lastHitBy: 0,
    lastHurt: -999,
    name: o.name ?? def.id,
    level: o.level ?? def.tier,
    def: def.id,
    tier: def.tier,
    affixes,
    elite,
    target: 0,
    atkCd: new Map(),
    windup: null,
    stateT: 0,
    homeX: pos.x,
    homeY: pos.y,
    xpValue: def.xp * (elite ? 3 : 1),
    phase: 0,
    minions: [],
    affixShield: 0,
    affixShieldCd: 0,
    owner: o.owner ?? 0,
    until: o.until ?? 0,
    wx: pos.x,
    wy: pos.y,
    scaleDamage: def.damage * (o.dmgScale ?? 1) * stats.damage,
    untargetable: false,
    isPet: false,
    petMul: 1,
  };
  if (affixes.includes('shielded')) m.affixShield = Math.round(m.maxHp * 0.3);
  if (def.thorns) m.stats.thorns = def.thorns;
  world.mobs.set(id, m);
  return m;
}

/** Spawns an account pet next to its owner: invulnerable, untargetable, never expires. */
export function spawnPet(world: World, owner: PlayerEnt, petId: string, level: number): MobEnt | null {
  const pdef = PETS[petId];
  const udef = pdef ? MOBS[pdef.unit] : undefined;
  if (!pdef || !udef) return null;
  const mul = petLevelMul(level);
  const m = spawnMob(world, udef.id, owner.x + 24, owner.y + 10, { team: owner.team, owner: owner.id, kind: 'summon', dmgScale: (0.55 + 0.06 * owner.level) * mul, level });
  m.isPet = true;
  m.untargetable = true;
  m.petMul = mul;
  m.name = petId;
  return m;
}

/** Spawns a player- or mob-owned summon. */
export function spawnSummon(world: World, owner: AnyUnit, unitId: string, x: number, y: number, duration: number): MobEnt | null {
  const def = MOBS[unitId];
  if (!def) return null;
  if (owner.kind === 'player') {
    const p = owner as PlayerEnt;
    p.summons = p.summons.filter((id) => {
      const s = world.mobs.get(id);
      return s && !s.dead;
    });
    const cap = 6 + Math.floor(p.stats.summonCount);
    while (p.summons.length >= cap) {
      const oldest = world.mobs.get(p.summons.shift()!);
      if (oldest) {
        oldest.dead = true;
        world.events.push({ t: 'death', id: oldest.id, kind: 'summon', x: oldest.x, y: oldest.y, def: oldest.def });
      }
    }
    const lvl = 0.7 + 0.1 * p.level;
    const m = spawnMob(world, unitId, x + world.rng.float(-30, 30), y + world.rng.float(-30, 30), {
      team: p.team,
      owner: p.id,
      kind: 'summon',
      until: world.tick + Math.round(duration * TPS),
      hpScale: lvl * p.stats.summonPower,
      dmgScale: lvl * p.stats.summonPower * Math.sqrt(p.stats.damage),
      level: p.level,
    });
    if (p.flags.has('summonsInheritArmor')) m.stats.armor += p.stats.armor * 0.5;
    m.stats.critChance = p.stats.critChance * 0.5;
    p.summons.push(m.id);
    world.events.push({ t: 'fx', kind: 'summon', x: m.x, y: m.y, team: m.team });
    return m;
  }
  // mob summoner or boss: minions are ordinary monsters
  const mo = owner as MobEnt;
  const m = spawnMob(world, unitId, x + world.rng.float(-40, 40), y + world.rng.float(-40, 40), { team: mo.team, hpScale: Math.max(1, mo.maxHp / (defOf(mo.def).hp || 1)) * 0.5, until: duration < 900 ? world.tick + Math.round(duration * TPS) : 0 });
  mo.minions.push(m.id);
  world.events.push({ t: 'fx', kind: 'summon', x: m.x, y: m.y, team: m.team });
  return m;
}

export function defOf(defId: string): MobDef {
  return MOBS[defId] ?? revenantDefs.get(defId) ?? MOBS.ghoul;
}

/** Builds a MobDef mimicking a fallen hero with the given weapon. */
function revenantDef(weaponId: string, heroColor: string): MobDef {
  const key = 'revenant:' + weaponId;
  const cached = revenantDefs.get(key);
  if (cached) return cached;
  const w = WEAPONS[weaponId] ?? WEAPONS.claymore;
  const melee = w.attack.type === 'area';
  const attack: MobAttack = melee
    ? { id: 'weapon', type: 'melee', cooldown: 1 / w.fireRate + 0.2, range: (w.attack as AreaAction).range ?? 90, damage: w.baseDamage, windup: 0.25, arc: (w.attack as AreaAction).arc ?? 2 }
    : { id: 'weapon', type: 'projectile', cooldown: 1 / w.fireRate + 0.15, range: 520, windup: 0.2, action: { ...(w.attack as ProjectileAction), damage: w.baseDamage } };
  const def: MobDef = {
    id: key,
    tier: 3,
    hp: 150,
    speed: melee ? 150 : 135,
    r: 14,
    damage: 10,
    xp: 60,
    behavior: melee ? 'chase' : 'ranged',
    keepDistance: melee ? undefined : 240,
    aggroRange: 700,
    attacks: [attack],
    color: heroColor,
    accent: '#ffffff',
    size: 30,
    shape: 'knight',
    elite: true,
  };
  revenantDefs.set(key, def);
  return def;
}

export function spawnRevenant(world: World, ghost: GhostSnapshot, x: number, y: number, tier: number): MobEnt {
  const hero = HEROES[ghost.heroId] ?? HEROES.revenant;
  const def = revenantDef(ghost.weapon, hero.accent);
  const lvl = Math.max(1, ghost.level);
  const hpScale = ((120 + lvl * 42) / def.hp) * (0.8 + tier * 0.25);
  const m = spawnMob(world, def.id, x, y, { kind: 'revenant', name: ghost.name, hpScale, dmgScale: (playerPower(lvl) * 0.9) / def.damage, level: lvl });
  m.ghost = ghost;
  m.xpValue = 40 + lvl * 7;
  m.revActives = ghost.actives.filter((a) => SKILLS[a.id]?.action).slice(0, 3).map((a) => ({ id: a.id, level: a.level, aspects: [...(a.aspects ?? [])], cd: Math.round(TPS * 2) }));
  m.stats.armor += Math.min(30, lvl * 1.5);
  return m;
}

// ------------------------------------------------------------------ stepping

export function stepMobs(world: World): void {
  for (const m of world.mobs.values()) {
    if (m.dead) continue;
    if (m.until && world.tick >= m.until) {
      m.dead = true;
      world.events.push({ t: 'death', id: m.id, kind: m.kind, x: m.x, y: m.y, def: m.def });
      continue;
    }
    if (m.isPet) {
      const owner = world.players.get(m.owner);
      if (!owner || owner.dead || owner.pet?.entityId !== m.id) {
        m.dead = true;
        continue;
      }
      // pet damage grows with the hero; keep it bounded by pet level
      m.scaleDamage = defOf(m.def).damage * (0.55 + 0.06 * owner.level) * m.petMul;
      const d = dist(m.x, m.y, owner.x, owner.y);
      if (d > 700) {
        m.x = owner.x + 20;
        m.y = owner.y + 10;
        world.events.push({ t: 'fx', kind: 'blink', x: m.x, y: m.y, team: m.team });
      }
    }
    stepMob(world, m);
  }
}

function acquireTarget(world: World, m: MobEnt, def: MobDef): AnyUnit | undefined {
  const taunt = m.statuses.find((s) => s.id === 'taunt');
  if (taunt) {
    const t = world.unit(taunt.source);
    if (t && !t.dead && t.team !== m.team) return t;
  }
  const cur = m.target ? world.unit(m.target) : undefined;
  if (cur && !cur.dead && cur.team !== m.team && world.tick % 10 !== 0) {
    if (dist(cur.x, cur.y, m.x, m.y) < def.aggroRange * 1.6) return cur;
  }
  if (m.kind === 'summon') {
    if (def.aggroRange <= 0) return undefined;
    const owner = world.unit(m.owner);
    const ox = owner ? owner.x : m.x;
    const oy = owner ? owner.y : m.y;
    // pets are leashed to their owner
    if (m.isPet && owner && dist(m.x, m.y, ox, oy) > 380) return undefined;
    // prefer enemies near the summon, else near the owner
    return world.nearestEnemy(m.x, m.y, def.aggroRange, m.team) ?? world.nearestEnemy(ox, oy, def.aggroRange, m.team);
  }
  // monsters prefer players over summons
  const players = world.enemiesNear(m.x, m.y, def.aggroRange, m.team);
  const pl = players.find((u) => u.kind === 'player');
  return pl ?? players[0];
}

function stepMob(world: World, m: MobEnt): void {
  const def = defOf(m.def);
  const dt = world.dt;
  const stunned = isStunned(m);
  if (stunned) m.windup = null;

  // boss phases
  if (def.boss) {
    const ph = def.boss.phases;
    while (m.phase + 1 < ph.length && m.hp / m.maxHp < ph[m.phase + 1].hpBelow) {
      m.phase++;
      const enter = ph[m.phase].onEnter;
      if (enter) runAction(world, enter, { caster: m, x: m.x, y: m.y, aimX: m.x, aimY: m.y, power: m.scaleDamage, mul: 1 });
      world.events.push({ t: 'announce', key: 'announce.bossPhase', vars: { boss: def.id, phase: m.phase + 1 }, color: '#ff9a3c' });
      world.events.push({ t: 'shake', id: m.id, power: 1 });
    }
  }
  // shielded affix regen
  if (m.affixes.includes('shielded') && m.affixShield <= 0) {
    if (m.affixShieldCd <= 0) m.affixShieldCd = world.seconds(6);
    else if (--m.affixShieldCd <= 0) m.affixShield = Math.round(m.maxHp * 0.3);
  }

  const target = stunned ? undefined : acquireTarget(world, m, def);
  m.target = target ? target.id : 0;

  // windup resolution
  if (m.windup && world.tick >= m.windup.until) {
    const atk = def.attacks.find((a) => a.id === m.windup!.attack) ?? revActive(m, m.windup.attack);
    const w = m.windup;
    m.windup = null;
    if (atk) executeAttack(world, m, def, atk, target, w.tx, w.ty);
  }

  // choose an attack
  if (!m.windup && !stunned && target) {
    const d = dist(m.x, m.y, target.x, target.y) - target.r;
    for (const atk of def.attacks) {
      if (atk.minPhase !== undefined && m.phase < atk.minPhase) continue;
      const cd = m.atkCd.get(atk.id) ?? 0;
      if (world.tick < cd) continue;
      if (d > atk.range) continue;
      startAttack(world, m, atk, target);
      break;
    }
    if (!m.windup && m.revActives) {
      for (const s of m.revActives) {
        if (s.cd > 0) {
          s.cd--;
          continue;
        }
        if (d > 480) continue;
        const sdef = SKILLS[s.id];
        if (!sdef) continue;
        const sc = scaledSkill(sdef, s);
        s.cd = Math.round(sc.cooldown * 1.4 * TPS);
        startAttack(world, m, { id: 'rev:' + s.id, type: 'area', cooldown: sc.cooldown, range: 480, windup: 0.5 }, target);
        break;
      }
    }
  }

  // movement
  let vx = 0;
  let vy = 0;
  const spd = m.stats.moveSpeed * speedMul(m) * (def.boss ? def.boss.phases[m.phase]?.speedMul ?? 1 : 1) * (m.affixes.includes('berserk') && m.hp < m.maxHp * 0.5 ? 1.4 : 1);
  const winding = !!m.windup;
  if (!stunned && !winding && spd > 0) {
    if (target) {
      const dx = target.x - m.x;
      const dy = target.y - m.y;
      const d = Math.hypot(dx, dy);
      const ux = d > 0 ? dx / d : 0;
      const uy = d > 0 ? dy / d : 0;
      const melee = def.attacks.find((a) => a.type === 'melee');
      const stopAt = melee ? melee.range * 0.75 + target.r : 0;
      switch (def.behavior) {
        case 'chase':
        case 'tank':
        case 'charger':
        case 'follow':
          if (d > stopAt) {
            vx = ux * spd;
            vy = uy * spd;
          }
          break;
        case 'guard': {
          const post = guardPost(world, m);
          const far = post ? dist(target.x, target.y, post.x, post.y) > 170 : false;
          if (far && post) {
            const pdx = post.x - m.x;
            const pdy = post.y - m.y;
            const pd = Math.hypot(pdx, pdy);
            if (pd > 6) {
              vx = (pdx / pd) * spd;
              vy = (pdy / pd) * spd;
            }
          } else if (d > stopAt) {
            vx = ux * spd;
            vy = uy * spd;
          }
          break;
        }
        case 'swarm': {
          const wob = Math.sin(world.tick * 0.35 + m.id) * 0.7;
          const ax = ux * Math.cos(wob) - uy * Math.sin(wob);
          const ay = ux * Math.sin(wob) + uy * Math.cos(wob);
          if (d > stopAt) {
            vx = ax * spd;
            vy = ay * spd;
          }
          break;
        }
        case 'ranged':
        case 'summoner':
        case 'teleport': {
          const keep = def.keepDistance ?? 250;
          if (def.behavior === 'teleport' && d < 150 && m.stateT <= 0) {
            const a = world.rng.angle();
            const p = world.map.randomPointNear(world.rng, target.x, target.y, 260, 340, m.r) ?? { x: m.x + Math.cos(a) * 280, y: m.y + Math.sin(a) * 280 };
            world.events.push({ t: 'fx', kind: 'blink', x: m.x, y: m.y, x2: p.x, y2: p.y, team: m.team });
            m.x = p.x;
            m.y = p.y;
            m.stateT = world.seconds(4);
          }
          if (d < keep * 0.8) {
            vx = -ux * spd;
            vy = -uy * spd;
          } else if (d > keep * 1.25) {
            vx = ux * spd;
            vy = uy * spd;
          } else {
            // strafe
            const s = Math.sin(world.tick * 0.05 + m.id) > 0 ? 1 : -1;
            vx = -uy * spd * 0.5 * s;
            vy = ux * spd * 0.5 * s;
          }
          break;
        }
        case 'turret':
          break;
      }
      if (d > 0) m.facing = Math.atan2(dy, dx);
    } else if (def.behavior === 'guard') {
      const post = guardPost(world, m);
      if (post) {
        const dx = post.x - m.x;
        const dy = post.y - m.y;
        const d = Math.hypot(dx, dy);
        if (d > 6) {
          const s = Math.min(spd * 1.2, d * 8);
          vx = (dx / d) * s;
          vy = (dy / d) * s;
          m.facing = Math.atan2(dy, dx);
        }
      }
    } else if (def.behavior === 'follow') {
      const owner = world.unit(m.owner);
      if (owner) {
        // pets trail slightly behind and to the side of the owner
        const tx = m.isPet ? owner.x - Math.cos(owner.facing) * 26 + Math.sin(owner.facing) * 22 : owner.x;
        const ty = m.isPet ? owner.y - Math.sin(owner.facing) * 26 - Math.cos(owner.facing) * 22 : owner.y;
        const dx = tx - m.x;
        const dy = ty - m.y;
        const d = Math.hypot(dx, dy);
        const stop = m.isPet ? 14 : 90;
        if (d > stop) {
          const s = m.isPet ? Math.min(spd, d * 6) : spd;
          vx = (dx / d) * s;
          vy = (dy / d) * s;
          m.facing = Math.atan2(dy, dx);
        }
      }
    } else if (def.behavior !== 'turret' && m.kind !== 'summon') {
      // wander near home
      if (m.stateT <= 0 || dist(m.x, m.y, m.wx, m.wy) < 12) {
        const p = world.map.randomPointNear(world.rng, m.homeX, m.homeY, 40, 220, m.r);
        if (p) {
          m.wx = p.x;
          m.wy = p.y;
        }
        m.stateT = world.seconds(world.rng.float(2, 5));
      }
      const dx = m.wx - m.x;
      const dy = m.wy - m.y;
      const d = Math.hypot(dx, dy);
      if (d > 4) {
        vx = (dx / d) * spd * 0.4;
        vy = (dy / d) * spd * 0.4;
        m.facing = Math.atan2(dy, dx);
      }
    }
  }
  if (m.stateT > 0) m.stateT--;
  // separation from other mobs (cheap): nudge away from the nearest ally overlapping
  if (world.tick % 3 === m.id % 3 && spd > 0 && !m.isPet) {
    const near = world.alliesNear(m.x, m.y, m.r + 12, m.team);
    for (const o of near) {
      if (o === m || o.kind === 'player') continue;
      const dx = m.x - o.x;
      const dy = m.y - o.y;
      const d = Math.hypot(dx, dy) || 1;
      const overlap = m.r + o.r - d;
      if (overlap > 0) {
        m.knockX += (dx / d) * overlap * 6;
        m.knockY += (dy / d) * overlap * 6;
      }
    }
  }
  m.vx = vx;
  m.vy = vy;
  const nx = m.x + (vx + m.knockX) * dt;
  const ny = m.y + (vy + m.knockY) * dt;
  m.knockX *= 0.8;
  m.knockY *= 0.8;
  const res = world.map.resolveCircle(nx, ny, m.r);
  m.x = res.x;
  m.y = res.y;

  // contact damage (bats)
  if (def.contactDamage && target && !stunned && world.tick % 10 === 0) {
    if (dist(m.x, m.y, target.x, target.y) < m.r + target.r + 2) dealDamage(world, m, target, m.scaleDamage * 0.5, ['blunt']);
  }
}

/** Formation slot of a guard summon: an evenly spaced, slowly turning ring around the owner. */
function guardPost(world: World, m: MobEnt): { x: number; y: number } | null {
  const owner = world.players.get(m.owner);
  if (!owner) return null;
  const guards = owner.summons.filter((id) => {
    const g = world.mobs.get(id);
    return g && !g.dead && defOf(g.def).guard;
  });
  const n = Math.max(1, guards.length);
  const i = Math.max(0, guards.indexOf(m.id));
  const a = (i / n) * Math.PI * 2 + world.tick * 0.012;
  const r = 62 + Math.min(30, n * 6);
  return { x: owner.x + Math.cos(a) * r, y: owner.y + Math.sin(a) * r };
}

function revActive(m: MobEnt, attackId: string): MobAttack | undefined {
  if (!attackId.startsWith('rev:')) return undefined;
  return { id: attackId, type: 'area', cooldown: 1, range: 480 };
}

function startAttack(world: World, m: MobEnt, atk: MobAttack, target: AnyUnit): void {
  const windup = Math.max(0, atk.windup ?? 0);
  m.atkCd.set(atk.id, world.tick + Math.round(atk.cooldown * TPS * (m.affixes.includes('berserk') ? 0.7 : 1)));
  m.facing = Math.atan2(target.y - m.y, target.x - m.x);
  if (windup <= 0) {
    executeAttack(world, m, defOf(m.def), atk, target, target.x, target.y);
    return;
  }
  const until = world.tick + Math.round(windup * TPS);
  m.windup = { attack: atk.id, until, tx: target.x, ty: target.y };
  // telegraphs
  if (atk.type === 'melee') {
    world.events.push({ t: 'telegraph', x: m.x, y: m.y, r: atk.range + 10, until, kind: 'circle' });
  } else if (atk.type === 'charge') {
    const d = atk.charge?.distance ?? 300;
    world.events.push({ t: 'telegraph', x: m.x, y: m.y, x2: m.x + Math.cos(m.facing) * d, y2: m.y + Math.sin(m.facing) * d, r: atk.charge?.radius ?? 30, until, kind: 'line' });
  } else if (atk.type === 'area' && atk.action && atk.action.type === 'area') {
    const at = atk.action.at === 'self' ? { x: m.x, y: m.y } : { x: target.x, y: target.y };
    world.events.push({ t: 'telegraph', x: at.x, y: at.y, r: atk.action.radius, until, kind: 'circle' });
  } else if (atk.type === 'projectile') {
    world.events.push({ t: 'telegraph', x: m.x, y: m.y, x2: target.x, y2: target.y, r: 6, until, kind: 'line' });
  } else if (atk.type === 'summon') {
    world.events.push({ t: 'telegraph', x: m.x, y: m.y, r: 60, until, kind: 'circle' });
  } else if (atk.id.startsWith('rev:')) {
    world.events.push({ t: 'telegraph', x: m.x, y: m.y, r: 40, until, kind: 'circle' });
  }
}

function affixStatuses(m: MobEnt): StatusApply[] {
  const out: StatusApply[] = [];
  for (const a of m.affixes) {
    const eff = MOB_AFFIXES[a]?.effect;
    if (eff === 'toxic') out.push({ id: 'blight', duration: 4, stacks: 2 });
    if (eff === 'burning') out.push({ id: 'burn', duration: 3, stacks: 1 });
    if (eff === 'frost') out.push({ id: 'chill', duration: 3, stacks: 2 });
  }
  return out;
}

function executeAttack(world: World, m: MobEnt, def: MobDef, atk: MobAttack, target: AnyUnit | undefined, tx: number, ty: number): void {
  const power = m.scaleDamage;
  const extra = affixStatuses(m);
  const tagsFor = (): Tag[] => (def.shape === 'knight' || def.shape === 'boss' ? ['slash'] : ['blunt']);
  switch (atk.type) {
    case 'melee': {
      const arc = atk.arc ?? 1.6;
      const reach = atk.range + 12;
      for (const e of world.enemiesNear(m.x, m.y, reach + 30, m.team)) {
        const d = dist(m.x, m.y, e.x, e.y) - e.r;
        if (d > reach) continue;
        const da = Math.abs(wrapAngle(Math.atan2(e.y - m.y, e.x - m.x) - m.facing));
        if (da > arc / 2 && d > 16) continue;
        const dealt = dealDamage(world, m, e, power * (atk.damage ?? 1), tagsFor());
        if (!e.dead) for (const s of extra) applyStatus(world, e, s, m, power);
        if (dealt > 0 && m.affixes.includes('vampiric')) heal(world, m, dealt * 0.4, null);
      }
      world.events.push({ t: 'fx', kind: def.boss ? 'swing_boss' : 'swing_mob', x: m.x, y: m.y, r: reach, a: m.facing, team: m.team, id: m.id });
      return;
    }
    case 'projectile': {
      if (!atk.action) return;
      let action: Action = atk.action;
      if (extra.length && action.type === 'projectile') action = { ...action, status: [...(action.status ?? []), ...extra] };
      runAction(world, action, { caster: m, x: m.x, y: m.y, aimX: target ? target.x : tx, aimY: target ? target.y : ty, target, power, mul: 1 });
      return;
    }
    case 'area': {
      if (atk.id.startsWith('rev:')) {
        const sid = atk.id.slice(4);
        const slot = m.revActives?.find((s) => s.id === sid);
        const sdef = SKILLS[sid];
        if (!slot || !sdef) return;
        const sc = scaledSkill(sdef, slot);
        if (!sc.action) return;
        runAction(world, sc.action, { caster: m, x: m.x, y: m.y, aimX: target ? target.x : tx, aimY: target ? target.y : ty, target, power: power * 0.8, mul: sc.mul * 0.8, countBonus: sc.countBonus, radiusBonus: sc.radiusBonus, durationBonus: sc.durationBonus, fromSkill: sid });
        world.events.push({ t: 'cast', id: m.id, skill: sid, x: m.x, y: m.y });
        return;
      }
      if (!atk.action) return;
      runAction(world, atk.action, { caster: m, x: m.x, y: m.y, aimX: tx, aimY: ty, power, mul: 1 });
      return;
    }
    case 'summon': {
      if (!atk.summon) return;
      m.minions = m.minions.filter((id) => {
        const u = world.mobs.get(id);
        return u && !u.dead;
      });
      const room = atk.summon.max - m.minions.length;
      for (let i = 0; i < Math.min(room, atk.summon.count); i++) spawnSummon(world, m, atk.summon.unit, m.x, m.y, 999);
      return;
    }
    case 'aura': {
      if (!atk.aura) return;
      const list = atk.aura.allies ? world.alliesNear(m.x, m.y, atk.aura.radius, m.team) : world.enemiesNear(m.x, m.y, atk.aura.radius, m.team);
      for (const u of list) for (const s of atk.aura.status) applyStatus(world, u, s, m, power);
      world.events.push({ t: 'fx', kind: atk.aura.allies ? 'aura_buff' : 'aura_curse', x: m.x, y: m.y, r: atk.aura.radius, team: m.team });
      return;
    }
    case 'heal': {
      if (!atk.heal) return;
      const pct = atk.heal.pct * (m.isPet ? m.petMul : 1);
      let healed = false;
      for (const u of world.alliesNear(m.x, m.y, atk.heal.radius, m.team)) {
        if (u.hp >= u.maxHp) continue;
        if (m.isPet && u.kind !== 'player') continue;
        heal(world, u, u.maxHp * pct, null);
        healed = true;
      }
      if (m.isPet && !healed) return;
      world.events.push({ t: 'fx', kind: 'heal', x: m.x, y: m.y, r: atk.heal.radius, team: m.team });
      return;
    }
    case 'charge': {
      const c = atk.charge ?? { distance: 300, radius: 30 };
      runAction(world, { type: 'dash', distance: c.distance, damage: atk.damage ?? 1, radius: c.radius, tags: tagsFor(), status: [...(c.status ?? []), ...extra], visual: 'charge' }, { caster: m, x: m.x, y: m.y, aimX: tx, aimY: ty, power, mul: 1 });
      return;
    }
  }
}

// ------------------------------------------------------------------ death

export function onMobKilled(world: World, m: MobEnt, killer: AnyUnit | null): void {
  const def = defOf(m.def);
  // credit summon kills to the owner
  let killerPlayer: PlayerEnt | null = null;
  if (killer) {
    if (killer.kind === 'player') killerPlayer = killer as PlayerEnt;
    else if ((killer as MobEnt).kind === 'summon') killerPlayer = world.players.get((killer as MobEnt).owner) ?? null;
  }
  if (m.kind === 'summon') {
    const owner = world.players.get(m.owner);
    if (owner) {
      owner.summons = owner.summons.filter((id) => id !== m.id);
      fireHooks(world, owner, 'summonDeath', { x: m.x, y: m.y });
    }
    return;
  }
  // xp to everyone nearby
  for (const p of world.players.values()) {
    if (p.dead) continue;
    if (dist(p.x, p.y, m.x, m.y) <= 760) addXp(world, p, m.xpValue);
  }
  const weight = (m.tier === 1 ? 1 : m.tier === 2 ? 2.5 : 5) * (m.elite ? 2 : 1) * (m.kind === 'revenant' ? 1.5 : 1);
  addCorruption(world, weight);
  if (killerPlayer) {
    killerPlayer.kills++;
    fireHooks(world, killerPlayer, 'kill', { target: m, tags: [], x: m.x, y: m.y });
    if (killerPlayer.flags.has('killExplode')) runAction(world, { type: 'area', at: 'target', radius: 85, damage: 1.2, tags: ['fire', 'area'], visual: 'explosion_fire', status: [{ id: 'burn', duration: 2, stacks: 1 }] }, { caster: killerPlayer, x: killerPlayer.x, y: killerPlayer.y, aimX: m.x, aimY: m.y, target: m, power: killerPlayer.power, mul: 1, depth: 1 });
    if (killerPlayer.flags.has('necromancer') && m.kind === 'mob' && world.rng.chance(0.3)) spawnSummon(world, killerPlayer, 'skeleton', m.x, m.y, 15);
    if (killerPlayer.flags.has('dashResetOnKill')) {
      killerPlayer.dashCharges = Math.max(1, Math.floor(killerPlayer.stats.dashCharges));
      killerPlayer.dashCd = 0;
    }
    if (killerPlayer.flags.has('blightSpreads')) {
      const st = stacksOf(m, 'blight');
      if (st > 0) for (const e of world.enemiesNear(m.x, m.y, 130, killerPlayer.team).slice(0, 8)) applyStatus(world, e, { id: 'blight', duration: 4, stacks: st }, killerPlayer, killerPlayer.power);
    }
    if (hasStatus(m, 'burn') && killerPlayer.traits.some((t) => t.id === 'pyromancer')) {
      for (const e of world.enemiesNear(m.x, m.y, 90, killerPlayer.team).slice(0, 6)) applyStatus(world, e, { id: 'burn', duration: 3, stacks: 1 }, killerPlayer, killerPlayer.power);
    }
  }
  // affixes on death
  if (m.affixes.includes('explosive')) {
    world.events.push({ t: 'fx', kind: 'explosion_fire', x: m.x, y: m.y, r: 95, team: m.team });
    for (const e of world.enemiesNear(m.x, m.y, 95, m.team)) dealDamage(world, null, e, m.scaleDamage * 2, ['fire']);
  }
  if (def.split && m.kind === 'mob') {
    for (let i = 0; i < def.split.count; i++) {
      const a = (i / def.split.count) * Math.PI * 2;
      const s = spawnMob(world, def.split.unit, m.x + Math.cos(a) * 24, m.y + Math.sin(a) * 24, { team: m.team, hpScale: m.maxHp / (def.hp || 1) / (m.elite ? 2.2 : 1) });
      s.knockX = Math.cos(a) * 200;
      s.knockY = Math.sin(a) * 200;
    }
  }
  dropLoot(world, m, killerPlayer);
  if (m.kind === 'boss') {
    world.events.push({ t: 'feed', key: 'feed.bossSlain', vars: { boss: m.def, killer: killerPlayer?.name ?? '' } });
    for (const p of world.players.values()) {
      if (p.dead) continue;
      p.bossKills++;
      addRankXp(world, p, BOSS_RANK_XP);
    }
    onBossKilledRealm(world, m, killer);
    world.listeners.onBossKilled?.(m, killer);
  } else if (m.kind === 'revenant') {
    if (killerPlayer) {
      killerPlayer.revenantKills++;
      killerPlayer.fameEarned += 15;
      // the fallen hero's relic: one roll per ghost life, never back to its own account
      const g = m.ghost;
      if (g && g.item && !g.itemRolled && killerPlayer.accountId !== g.accountId) {
        g.itemRolled = true;
        if (world.rng.chance(REVENANT_ITEM_CHANCE)) {
          const relic = itemFromSave(world, g.item);
          if (relic) {
            spawnPickup(world, m.x, m.y + 24, { type: 'item', item: relic, owner: killerPlayer.id, ttl: 120 });
            world.events.push({ t: 'feed', key: 'feed.relicDrop', vars: { name: m.name, killer: killerPlayer.name, item: relic.def } });
          }
        }
      }
    }
    world.events.push({ t: 'feed', key: 'feed.revenantSlain', vars: { name: m.name, killer: killerPlayer?.name ?? '' } });
    world.listeners.onRevenantKilled?.(m, killer);
  }
}
